import { useRef, useState } from 'react';

import FormikTextInput from '@perpay-web/components/base/TextInput/FormikTextInput';
import { KeyboardKey } from '@perpay-web/utils/keyboardUtils';
import { noop } from '@perpay-web/utils/noop';

import styles from './Combobox.scss';

/**
 * Implements a WAI-ARIA compliant combo-box widget with manual selection.
 * https://www.w3.org/TR/wai-aria-practices/examples/combobox/aria1.1pattern/listbox-combo.html
 */
const Combobox = ({
    children,
    expanded,
    getValueAtIndex,
    highlightedIndex = null,
    id,
    label,
    loading,
    name,
    onArrowDown,
    onArrowUp,
    onFocus,
    onBlur,
    onEscape,
    onHighlightIndex,
    onInputValueChange,
    onSelect,
    placeholder,
    selectedItem = null,
    ...rest
}) => {
    const renderFunc = children;
    const inputRef = useRef();

    return (
        <div className={styles.combobox}>
            <FormikTextInput
                onFocus={onFocus}
                onBlur={onBlur}
                ref={inputRef}
                label={label}
                type='text'
                loading={loading}
                onChange={(e) => {
                    onInputValueChange(e.target.value);
                }}
                name={name}
                aria-activedescendant={
                    typeof highlightedIndex === 'number'
                        ? `${id}-option-${highlightedIndex}`
                        : null
                }
                aria-autocomplete='list'
                aria-controls={`${id}-listbox`}
                aria-expanded={expanded}
                aria-haspopup='listbox'
                aria-live='polite'
                aria-owns={`${id}-listbox`}
                onKeyDown={(event) => {
                    switch (event.key) {
                        case KeyboardKey.ARROW_DOWN:
                            onArrowDown(highlightedIndex);
                            break;
                        case KeyboardKey.ARROW_UP:
                            onArrowUp(highlightedIndex);
                            break;
                        case KeyboardKey.ESCAPE:
                            onEscape();
                            break;
                        case KeyboardKey.ENTER: {
                            const highlightedItem =
                                getValueAtIndex(highlightedIndex);
                            onSelect(highlightedItem && highlightedItem.text);
                            break;
                        }
                        default:
                            break;
                    }
                }}
                placeholder={placeholder}
                role='combobox'
                {...rest}
            />
            <div
                className={styles.combobox__results}
                hidden={!expanded}
                role='listbox'
                id={`${id}-listbox`}
            >
                {renderFunc({
                    onHighlightIndex,
                    onSelect,
                    highlightedIndex,
                    selectedItem,
                    getItemId: (index) => `${id}-option-${index}`,
                    getHighlightedCss: () =>
                        styles['combobox__result--highlighted'],
                    getSelectedCss: () => styles['combobox__result--selected'],
                    getResultCss: () => styles.combobox__result,
                })}
            </div>
        </div>
    );
};

const StatefulCombobox = ({
    value,
    onInputValueChange = noop,
    onFocus = noop,
    onBlur = noop,
    itemCount,
    onSelect = noop,
    getValueAtIndex,
    children,
    label = '',
    id,
    loading = false,
    placeholder = '',
    name,
    ...rest
}) => {
    const [expanded, setExpanded] = useState(false);
    const [highlightedIndex, setHighlightedIndex] = useState(null);
    const [selectedItem, setSelectedItem] = useState(value);

    const onHighlightIndex = (index) => {
        setHighlightedIndex(index);
    };

    const onInputValueChangeInternal = (newValue) => {
        setExpanded(true);
        setHighlightedIndex(null);
        setSelectedItem(null);
        onInputValueChange(newValue);
    };

    const onArrowDown = (currentIndex) => {
        const baseIndex = currentIndex === null ? -1 : currentIndex;
        setExpanded(true);
        setHighlightedIndex((baseIndex + 1) % itemCount);
    };

    const onArrowUp = (currentIndex) => {
        const baseIndex = currentIndex === null ? itemCount : currentIndex;
        const newIndex = baseIndex - 1;
        setExpanded(true);
        setHighlightedIndex(newIndex < 0 ? itemCount - 1 : newIndex);
    };

    const onEscape = () => {
        setExpanded(false);
    };

    const onSelectInternal = (newValue) => {
        if (!newValue) {
            return;
        }

        onSelect(newValue);
        setExpanded(false);
        setSelectedItem(newValue);
    };

    return (
        <Combobox
            expanded={expanded}
            getValueAtIndex={getValueAtIndex}
            highlightedIndex={highlightedIndex}
            id={id}
            label={label}
            loading={loading}
            name={name}
            onArrowDown={(...args) => onArrowDown(...args)}
            onArrowUp={(...args) => onArrowUp(...args)}
            onEscape={(...args) => onEscape(...args)}
            onFocus={(...args) => onFocus(...args)}
            onBlur={(...args) => onBlur(...args)}
            onHighlightIndex={(...args) => onHighlightIndex(...args)}
            onInputValueChange={(...args) =>
                onInputValueChangeInternal(...args)
            }
            onSelect={(...args) => onSelectInternal(...args)}
            placeholder={placeholder}
            selectedItem={selectedItem}
            {...rest}
        >
            {children}
        </Combobox>
    );
};

export default StatefulCombobox;
